/**
 * =============================================================================
 * Redaktionsplan.js — Admin-Dashboard: Marketing-Cockpit (Tabelle red_redaktionsplan)
 * Erfasst ALLE Marketing-Aktionen: planen · aktivieren · bewerten · auswerten.
 *   Kanal · Status(geplant/aktiv/erledigt) · Prognose · Kern-Ergebnis
 *   E-Mail-Detail (Versand/Öffn/Klicks/Abm) + Funnel-Raten (Klick/Abm = /Öffnungen).
 * Editieren: ☑ Haken vorne = Zeile editierbar, Haken raus = speichern. ➕ einfügen · 🗑 löschen.
 * Raten sind BERECHNET (read-only, farbcodiert: Öffn grün · Klick gelb · Abm rot).
 * API: /api/redaktionsplan.php (GET/POST/PUT/DELETE, Bearer).
 * VERSION: v3.0  DATUM: 2026-07-01
 * =============================================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const API = '/api/redaktionsplan.php';
const authHeaders = () => ({
  Authorization: 'Bearer ' + (localStorage.getItem('token') || ''),
  'Content-Type': 'application/json',
});

const KANAL = ['E-Mail', 'Zoom-Lesung', 'WG-Webinar', 'Clip/Video', 'Amazon-Ad', 'Social', 'Blog/SEO', 'Sonstiges'];
const STATUS = ['geplant', 'aktiv', 'erledigt'];

const RATE_OF = { oeffnungsrate: 'oeffnungen', klickrate: 'klicks', abmeldungsrate: 'abmeldungen' };
// Funnel-Nenner: Öffn. /Versand · Klick + Abm. /Öffnungen (man muss öffnen, um zu klicken/abzumelden).
const RATE_DEN = { oeffnungsrate: 'versand_n', klickrate: 'oeffnungen', abmeldungsrate: 'oeffnungen' };
const RATE_COLOR = {
  oeffnungsrate: { bg: '#dcfce7', ink: '#15803d', head: '#16a34a' },
  klickrate:     { bg: '#fef9c3', ink: '#a16207', head: '#eab308' },
  abmeldungsrate:{ bg: '#fee2e2', ink: '#b91c1c', head: '#ef4444' },
};
const STATUS_BADGE = {
  geplant:  { bg: '#e5e7eb', ink: '#374151' },
  aktiv:    { bg: '#dbeafe', ink: '#1e40af' },
  erledigt: { bg: '#dcfce7', ink: '#166534' },
};

const COLS = [
  { k: 'quentn_id', label: 'Q-ID', type: 'number', w: 54 },
  { k: 'kanal', label: 'Kanal', type: 'select', opts: KANAL, w: 108 },
  { k: 'status', label: 'Status', type: 'select', opts: STATUS, w: 94 },
  { k: 'zielgruppe', label: 'Zielgruppe', type: 'text', w: 150 },
  { k: 'stand', label: 'Stand', type: 'date', w: 100 },
  { k: 'betreff', label: 'Betreff / Aktion', type: 'text', w: 200 },
  { k: 'body', label: 'Text / Body', type: 'textarea', w: 320 },
  { k: 'versand_datum', label: 'Versand', type: 'date', w: 100 },
  { k: 'versand_uhrzeit', label: 'Uhrzeit', type: 'time', w: 76 },
  { k: 'versand_n', label: '# Versand', type: 'number', w: 68 },
  { k: 'oeffnungen', label: '# Öffn.', type: 'number', w: 60 },
  { k: 'oeffnungsrate', label: 'Öffn.-%', type: 'rate', w: 64 },
  { k: 'klicks', label: '# Klicks', type: 'number', w: 60 },
  { k: 'klickrate', label: 'Klick-%', type: 'rate', w: 64 },
  { k: 'abmeldungen', label: '# Abm.', type: 'number', w: 60 },
  { k: 'abmeldungsrate', label: 'Abm.-%', type: 'rate', w: 64 },
  { k: 'prognose', label: 'Prognose', type: 'textarea', w: 240 },
  { k: 'kern_ergebnis', label: 'Kern-Ergebnis', type: 'text', w: 180 },
  { k: 'bemerkung', label: 'Bemerkung / Erkenntnis', type: 'textarea', w: 280 },
  { k: 'gut_schlecht', label: 'Gut / Schlecht', type: 'textarea', w: 220 },
];

const C = { ink: '#000000', head: '#4f46e5', headText: '#fff', grid: '#94a3b8', zebra: '#dbe3ee', hover: '#cdd9f2', edit: '#fff3d6' };

const pct = (cnt, vn) => { const c = parseFloat(cnt), v = parseFloat(vn); return v > 0 && !isNaN(c) ? (c / v * 100).toFixed(2) : ''; };
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
const WD = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const weekday = (s) => { if (!s) return ''; const d = new Date(String(s).slice(0, 10) + 'T00:00:00'); return isNaN(d.getTime()) ? '' : WD[d.getDay()]; };
const fmtDate = (s) => { if (!s) return ''; const p = String(s).slice(0, 10).split('-'); return (p.length === 3) ? `${p[2]}.${p[1]}.${p[0]}` : s; };

export default function Redaktionsplan() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [hoverId, setHoverId] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetch(API, { headers: authHeaders() }).then(r => r.json())
      .then(d => { if (d.success) setRows(d.data || []); else setError(d.message || 'Fehler'); })
      .catch(e => setError(String(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => { const f = { ...row }; if (f.versand_uhrzeit) f.versand_uhrzeit = hhmm(f.versand_uhrzeit); setEditingId(row.id); setForm(f); };
  const cancelEdit = () => { setEditingId(null); setForm({}); };
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    setBusy(true);
    fetch(API, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(form) }).then(r => r.json())
      .then(d => { if (d.success) { setEditingId(null); setForm({}); load(); } else setError(d.message || 'Speichern fehlgeschlagen'); })
      .catch(e => setError(String(e))).finally(() => setBusy(false));
  };
  const addRow = (afterId = null) => {
    setBusy(true);
    fetch(API, { method: 'POST', headers: authHeaders(), body: JSON.stringify(afterId ? { after_id: afterId } : {}) }).then(r => r.json())
      .then(d => {
        if (d.success && d.data) { fetch(API, { headers: authHeaders() }).then(r => r.json()).then(dd => { if (dd.success) setRows(dd.data || []); startEdit(d.data); }); }
        else setError(d.message || 'Anlegen fehlgeschlagen');
      }).catch(e => setError(String(e))).finally(() => setBusy(false));
  };
  const del = (id) => {
    if (!window.confirm('Diese Zeile wirklich löschen?')) return;
    setBusy(true);
    fetch(API + '?id=' + id, { method: 'DELETE', headers: authHeaders() }).then(r => r.json())
      .then(d => { if (d.success) { if (editingId === id) cancelEdit(); load(); } else setError(d.message || 'Löschen fehlgeschlagen'); })
      .catch(e => setError(String(e))).finally(() => setBusy(false));
  };

  const th = { position: 'sticky', top: 0, background: C.head, color: C.headText, padding: '5px 6px', fontSize: 11, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', border: `1px solid ${C.head}`, zIndex: 2 };
  const thAct = { ...th, position: 'sticky', left: 0, zIndex: 4, minWidth: 120, textAlign: 'center' };
  const td = { padding: '2px 6px', border: `1px solid ${C.grid}`, fontSize: 12, verticalAlign: 'middle', color: C.ink };
  const tdAct = (bg) => ({ ...td, position: 'sticky', left: 0, background: bg, zIndex: 1, textAlign: 'center', whiteSpace: 'nowrap' });
  const inp = { width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '3px 5px', border: `1px solid #94a3b8`, borderRadius: 5, background: '#fff' };
  const icoBtn = (color, bg = '#fff') => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 25, height: 23, border: `1px solid ${color}44`, background: bg, color, borderRadius: 6, cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0, margin: '0 1px' });
  const badge = (bg, ink) => ({ background: bg, color: ink, borderRadius: 5, padding: '1px 7px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' });
  const addBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: '#22c55e', color: '#fff', borderRadius: 9, padding: '8px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 600, boxShadow: '0 1px 3px #22c55e55' };

  return (
    <div style={{ padding: '20px 18px', fontFamily: 'system-ui, -apple-system, sans-serif', color: C.ink, background: '#f1f5f9', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1800, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
          <Link to="/admin" style={{ textDecoration: 'none', color: '#6366f1', fontSize: 14, fontWeight: 500 }}>← Admin</Link>
          <h1 style={{ fontSize: 24, margin: 0 }}>📋 Redaktionsplan</h1>
          <button onClick={() => addRow(null)} disabled={busy} style={{ ...addBtn, opacity: busy ? 0.6 : 1 }}>＋ Neue Zeile</button>
          <button onClick={load} title="Neu laden" style={{ ...icoBtn('#6366f1'), marginLeft: 'auto', width: 34, height: 32, fontSize: 16 }}>↻</button>
        </div>
        <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 12px' }}>
          Marketing-Cockpit: <b>planen · aktivieren · bewerten · auswerten</b> &nbsp;·&nbsp; Raten (Funnel): Öffn-% /Versand, <b>Klick-% & Abm-% /Öffnungen</b> &nbsp;·&nbsp; <b>☑ Haken</b> = editieren, Haken raus = <b>speichern</b> &nbsp;·&nbsp; <b>➕</b> Zeile darunter, <b>🗑</b> löschen.
        </p>

        {error && <p style={{ color: '#dc2626', background: '#fef2f2', padding: '8px 12px', borderRadius: 8 }}>⚠ {error}</p>}
        {loading && <p style={{ color: '#64748b' }}>Lädt …</p>}

        <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 1px 4px rgba(15,23,42,0.10)', background: '#fff', border: `1px solid ${C.grid}` }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1760 }}>
            <thead>
              <tr>
                <th style={thAct}>Aktion</th>
                {COLS.map(c => { const rc = RATE_COLOR[c.k]; return <th key={c.k} style={{ ...th, minWidth: c.w, ...(rc ? { background: rc.head, border: `1px solid ${rc.head}` } : {}) }}>{c.label}</th>; })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={COLS.length + 1} style={{ ...td, color: '#94a3b8', textAlign: 'center', padding: 28 }}>Noch keine Einträge — mit „＋ Neue Zeile" starten. 🙂</td></tr>
              )}
              {rows.map((row, i) => {
                const editing = editingId === row.id;
                const baseBg = editing ? C.edit : (hoverId === row.id ? C.hover : (i % 2 ? C.zebra : '#fff'));
                return (
                  <tr key={row.id} onMouseEnter={() => setHoverId(row.id)} onMouseLeave={() => setHoverId(null)} style={{ background: baseBg }}>
                    <td style={tdAct(baseBg)}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        <input type="checkbox" checked={editing} disabled={busy}
                          title={editing ? 'Haken raus = speichern' : 'Haken setzen = Zeile editieren'}
                          onChange={e => (e.target.checked ? startEdit(row) : save())}
                          style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#4f46e5', marginRight: 2 }} />
                        <button onClick={() => addRow(row.id)} disabled={busy} title="Zeile darunter einfügen" style={icoBtn('#16a34a')}>➕</button>
                        <button onClick={() => del(row.id)} title="Löschen" style={icoBtn('#dc2626')}>🗑</button>
                      </div>
                    </td>
                    {COLS.map(c => {
                      if (c.type === 'rate') {
                        const rc = RATE_COLOR[c.k];
                        const val = editing ? pct(form[RATE_OF[c.k]], form[RATE_DEN[c.k]]) : (row[c.k] ?? '');
                        const show = (val !== '' && val != null) ? val + ' %' : '–';
                        return <td key={c.k} style={{ ...td, background: rc.bg, color: rc.ink, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{show}</td>;
                      }
                      if (!editing) {
                        if (c.type === 'select') {
                          const v = row[c.k] || '';
                          const b = (c.k === 'status' && STATUS_BADGE[v]) ? STATUS_BADGE[v] : { bg: '#eef2ff', ink: '#4338ca' };
                          return <td key={c.k} style={{ ...td, whiteSpace: 'nowrap' }}>{v ? <span style={badge(b.bg, b.ink)}>{v}</span> : ''}</td>;
                        }
                        if (c.type === 'date') {
                          const dv = row[c.k]; const wd = weekday(dv);
                          return <td key={c.k} style={{ ...td, whiteSpace: 'nowrap' }}>{dv ? <span>{wd && <b style={{ color: '#6366f1' }}>{wd}</b>}{wd ? ', ' : ''}{fmtDate(dv)}</span> : ''}</td>;
                        }
                        let v = row[c.k];
                        if (c.k === 'versand_uhrzeit') v = hhmm(v);
                        const num = c.type === 'number';
                        const wrap = c.k === 'betreff' || c.type === 'textarea';
                        return <td key={c.k} style={{ ...td, maxWidth: c.k === 'betreff' ? 230 : undefined, textAlign: num ? 'right' : 'left', fontVariantNumeric: num ? 'tabular-nums' : 'normal', whiteSpace: wrap ? (c.type === 'textarea' ? 'pre-wrap' : 'normal') : 'nowrap', wordBreak: c.k === 'betreff' ? 'break-word' : 'normal' }}>{v ?? ''}</td>;
                      }
                      // editing
                      if (c.type === 'select') {
                        return <td key={c.k} style={td}><select style={inp} value={form[c.k] ?? ''} onChange={e => setF(c.k, e.target.value)}><option value="">—</option>{c.opts.map(o => <option key={o} value={o}>{o}</option>)}</select></td>;
                      }
                      if (c.type === 'date') {
                        return <td key={c.k} style={td}><div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><input type="date" style={{ ...inp, flex: 1 }} value={form[c.k] ?? ''} onChange={e => setF(c.k, e.target.value)} /><span style={{ color: '#6366f1', fontWeight: 700, fontSize: 12, minWidth: 18 }}>{weekday(form[c.k])}</span></div></td>;
                      }
                      if (c.type === 'textarea') {
                        return <td key={c.k} style={td}><textarea style={{ ...inp, minHeight: 32, resize: 'vertical' }} value={form[c.k] ?? ''} onChange={e => setF(c.k, e.target.value)} /></td>;
                      }
                      return <td key={c.k} style={td}><input type={c.type === 'number' ? 'number' : c.type} step={c.type === 'number' ? '1' : undefined} style={{ ...inp, textAlign: c.type === 'number' ? 'right' : 'left' }} value={form[c.k] ?? ''} onChange={e => setF(c.k, e.target.value)} /></td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 12 }}>{rows.length} {rows.length === 1 ? 'Aktion' : 'Aktionen'} · Reihenfolge frei (Einfügen zwischen zwei Zeilen möglich).</p>
      </div>
    </div>
  );
}
